import { useContext, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import AutoComplete from '../../../components/AutoComplete/AutoComplete'
import { metodoPrograma } from '../../../components/api'
import { GlobalContext } from '../../../App'
import { useModal } from '../../../components/Utils/UseModal'
import ModalVisualizaDocumento from './ModalVisualizaDocumento'
import { Edit, FilterList } from '@mui/icons-material'
import { Printer } from 'lucide-react'
import FormattedTextEditor from '../../../components/FormattedTextEditor/FormattedTextEditor'
import FormattedHtml, {
  sanitizaHtml,
} from '../../../components/FormattedHtml/FormattedHtml'
import {
  geraPdfReceituarioSimples,
  normalizaConteudoReceituario,
} from './geraPdfReceituarioSimples'
import { geraPdfReceituarioEspecial } from './geraPdfReceituarioEspecial'
import ModalEdicaoMedicamento from './ModalEdicaoMedicamento'

const validationSchema = Yup.object({
  dsConteudo: Yup.string().required('Conteúdo é obrigatório'),
})

// A área útil do PDF mede 523pt e usa texto de 10,5pt.
// Mantém a largura mesmo em telas pequenas para não mudar a composição.
const estiloEditorReceituario = {
  boxSizing: 'content-box',
  width: '523pt',
  fontFamily: 'Arial, Helvetica, sans-serif',
  fontSize: '10.5pt',
  lineHeight: '17pt',
  color: '#1c1c1c',
  whiteSpace: 'normal',
  overflowWrap: 'normal',
  '& p, & div': { margin: 0 },
  '& ul, & ol': { margin: 0, paddingLeft: '14pt' },
}

const observacoesPadrao = `Observações para farmácia de manipulação:
Manipular todas as cápsulas, sem corantes artificiais, sem glúten, sem sabores artificiais, sem açúcar e sem polióis.

Observações para o paciente:
Sal Integral: Introduzir o sal integral em todas as refeições e na água de beber, SEMPRE uma pitada de sal.
Evitar forno de micro-ondas, panelas de teflon e alumínio; dar preferência à panelas de aço inox, vidro, cerâmica e pedra.
Reduzir/ retirar: açúcar, glúten, margarina, bebidas enriquecidas com cálcio, aspartame, plásticos (bisfenol A), leite longa vida.
Utilizar gel dental sem flúor, sem parabenos e sulfatos.
Utilizar desodorante sem alumínio.
Tomar, no mínimo, 2 litros de água por dia, dividir em tomadas de 30 em 30 minutos.
Não consumir líquidos durante as refeições.
Realizar exercícios físicos, no mínimo, 3 vezes por semana.
Dormir em quarto totalmente escuro, sem leds, longe do celular e silencioso.
Para melhorar a flora intestinal, sugiro tomar copo de 100ml (1/2 copo tipo americano) de Kombucha.

Sugestões de chás para ajudar no sono:
Mulungu, Melissa, Camomila, Lavanda.

Sugestões de chás para a digestão:
Achillea milefolium, Peumus boldus, Taraxacum officinalis.

Contato com a natureza, no mínimo, 1 vez por semana, prática de exercícios físicos ao ar livre e grounding (aterramento).

Para garantir uma melhor qualidade do sono, faça uma higiene do sono ao menos 1 hora antes do horário de dormir: desligue celular e TV, se houver música baixinha e tranquila, uma boa leitura, ambientes com luzes mais delicadas e sugiro o uso de vela.

Obs.: Sugerimos fazer contato conosco antes do término dos medicamentos (180 dias), para possível solicitação de exames laboratoriais e reavaliação por nossa equipe médica.

PROVÉRBIO ÁRABE
“O nosso inimigo não é aquele que nos odeia, mas sim aquele que nós odiamos.”`

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

const textoParaHtml = (value = '') => {
  if (/<\/?[a-z][\s\S]*>/i.test(value)) return value
  return escapeHtml(value).replace(/\n/g, '<br>')
}

const textoSemFormatacao = (value = '') =>
  value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/div>|<\/p>|<\/li>/gi, '\n')
    .replace(/<[^>]*>/g, '')

const Edicao = ({ dados, preenchido }) => {
  const { localAtendimento } = useContext(GlobalContext)
  const [estaAssinado, setEstaAssinado] = useState(dados?.[7] === '1')
  const [idReceituario, setIdReceituario] = useState(dados?.[0] || null)
  const [mostrarFiltros, setMostrarFiltros] = useState(false)
  const [abaEdicao, setAbaEdicao] = useState('conteudo')
  const tpReceituarioRef = useRef('S')

  const [paciente, setPaciente] = useState(
    preenchido?.acCliente
      ? preenchido.acCliente
      : dados
        ? [[dados[5]], dados[4]]
        : null,
  )
  const [erroPaciente, setErroPaciente] = useState('')
  const [buscaTexto, setBuscaTexto] = useState('')
  const [medicamentosBusca, setMedicamentosBusca] = useState([])
  const [observacaoMedico, setObservacaoMedico] = useState(null)

  const modalDocumento = useModal(ModalVisualizaDocumento)
  const modalEditarMedicamento = useModal(ModalEdicaoMedicamento)
  useEffect(() => {
    metodoPrograma('buscaMedicamento', { value: '' }).then((res) => {
      setMedicamentosBusca(res?.result || [])
    })
  }, [])

  useEffect(() => {
    let ativo = true

    metodoPrograma('dadosMedicoReceituario', {}, 'CtrlReceitaAvulsa').then(
      (res) => {
        if (ativo && res?.status) {
          setObservacaoMedico(res.dsObservacaoReceituario || '')
        }
      },
    )

    return () => {
      ativo = false
    }
  }, [])

  const handleBuscarMedicamento = async (texto, idTipoMedicamento) => {
    setBuscaTexto(texto)

    const payload = {
      value: texto || '',
    }

    if (idTipoMedicamento) {
      payload.idTipoMedicamento = idTipoMedicamento
    }

    const res = await metodoPrograma('buscaMedicamento', payload)

    setMedicamentosBusca(res?.result || [])
  }

  const handleEditarMedicamento = async (event, item) => {
    event.stopPropagation()

    const idMedicamento = item?.[0]?.[0]
    if (!idMedicamento) return

    const alterado = await modalEditarMedicamento({
      medicamento: {
        idMedicamento,
        nmMedicamento: item?.[1] || '',
        dsInformacoes: item?.[2] || '',
        dsPosologia: item?.[6] || '',
      },
    })

    if (!alterado) return

    setMedicamentosBusca((atual) =>
      atual.map((medicamento) => {
        if (medicamento?.[0]?.[0] !== alterado.idMedicamento) {
          return medicamento
        }

        const atualizado = [...medicamento]
        atualizado[1] = alterado.nmMedicamento
        atualizado[2] = alterado.dsInformacoes
        atualizado[6] = alterado.dsPosologia
        return atualizado
      }),
    )
  }

  const handleSelecionarMedicamento = (item, setFieldValue, conteudoAtual) => {
    const id = item?.[0]?.[0]
    const titulo = item?.[1]
    const descricao = item?.[2]
    const posologia = item?.[6]

    if (!id) return

    // procura padrões tipo:
    // 1)
    // 2)
    // 3)
    const matches = textoSemFormatacao(conteudoAtual).match(/^\d+\)/gm) || []

    // pega o maior número encontrado
    const ultimoNumero = matches.length
      ? Math.max(...matches.map((m) => parseInt(m.replace(')', ''))))
      : 0

    const proximoNumero = ultimoNumero + 1

    const blocoMedicamento =
      `<div><strong>${proximoNumero}) ${escapeHtml(titulo)}</strong></div>` +
      `<div>${textoParaHtml(descricao || '')}</div>` +
      `<div>${textoParaHtml(posologia || '')}</div>`

    const novoConteudo = conteudoAtual
      ? `${textoParaHtml(conteudoAtual)}<br>${blocoMedicamento}`
      : blocoMedicamento

    setFieldValue('dsConteudo', novoConteudo)
  }

  const onSubmit = async (values) => {
    if (!paciente?.[0]) {
      setErroPaciente('Paciente é obrigatório')
      return
    }
    setErroPaciente('')

    const payload = {
      idReceituario: idReceituario || undefined,
      idPaciente: paciente?.[0]?.[0],
      // Remove estilos auxiliares copiados de editores externos (por exemplo,
      // variáveis --tw-* do Tailwind), que não fazem parte do documento e
      // podem tornar a requisição grande demais para o servidor.
      dsConteudo: normalizaConteudoReceituario(sanitizaHtml(values.dsConteudo)),
      dsObservacao: normalizaConteudoReceituario(
        sanitizaHtml(values.dsObservacao),
      ),
      tpReceituario: tpReceituarioRef.current,
    }

    let res
    if (tpReceituarioRef.current === 'S' || tpReceituarioRef.current === 'E') {
      const dadosPaciente = await metodoPrograma(
        'dadosPacienteReceituario',
        { idPaciente: payload.idPaciente },
        'CtrlReceitaAvulsa',
      )
      if (!dadosPaciente?.status) return
      const validacao = await metodoPrograma(
        'qrCodeReceituario',
        { cdChaveAcessoExterno: dadosPaciente.cdChaveAcessoExterno },
        'CtrlReceitaAvulsa',
      )
      if (!validacao?.status) return
      res = await metodoPrograma(
        'emissaoReceituarioHTML',
        {
          ...payload,
          cdChaveAcessoExterno: dadosPaciente.cdChaveAcessoExterno,
          dsPdfBase64: await (
            tpReceituarioRef.current === 'E'
              ? geraPdfReceituarioEspecial
              : geraPdfReceituarioSimples
          )({
            paciente: dadosPaciente,
            dsConteudo: payload.dsConteudo,
            dsObservacao: payload.dsObservacao,
            qrCodeBase64: validacao.imagemBase64,
            urlValidacao: validacao.url,
          }),
        },
        'CtrlReceitaAvulsa',
      )
    }
    if (res?.status) {
      setIdReceituario(res.idReceituario)
      await modalDocumento({
        url: res?.dsUrlAssinado,
      })
      const situacao = await metodoPrograma(
        'situacaoAssinatura',
        { idReceituario: res.idReceituario },
        'CtrlReceitaAvulsa',
      )
      setEstaAssinado(situacao?.assinado === true)
    }
  }

  useEffect(() => {
    setEstaAssinado(dados?.[7] === '1')
    setIdReceituario(dados?.[0] || null)
  }, [dados])

  return (
    <Formik
      initialValues={{
        dsConteudo: preenchido?.dsConteudo || dados?.[3] || '',
        dsObservacao: observacaoMedico ?? observacoesPadrao,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        submitForm,
        errors,
        touched,
        isSubmitting,
      }) => (
        <Form>
          <Grid container spacing={2} style={{ height: '100%' }}>
            <Grid item xs={12} md={8}>
              <Box
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 16,
                  padding: 15,
                  height: '100%',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <AutoComplete
                      label="Paciente"
                      metodo="buscaPaciente"
                      value={paciente}
                      disabled={isSubmitting || estaAssinado}
                      onChange={(_, val) => {
                        setPaciente(val)
                        setErroPaciente('')
                      }}
                      searchParams={{
                        idLocalAtendimento: localAtendimento[0],
                      }}
                      fullWidth
                      size="small"
                      textFieldProps={{
                        error: !!erroPaciente,
                        helperText: erroPaciente,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Tabs
                      value={abaEdicao}
                      onChange={(_, value) => setAbaEdicao(value)}
                      variant="fullWidth"
                      sx={{
                        minHeight: 32,
                        '& .MuiTab-root': {
                          minHeight: 32,
                          minWidth: 0,
                          py: 0,
                          textTransform: 'none',
                          fontSize: '0.75rem',
                        },
                      }}
                    >
                      <Tab label="Conteúdo" value="conteudo" />
                      <Tab label="Observações" value="observacoes" />
                    </Tabs>

                    {abaEdicao === 'conteudo' ? (
                      <FormattedTextEditor
                        value={values.dsConteudo}
                        normalizarConteudo={normalizaConteudoReceituario}
                        editorSx={estiloEditorReceituario}
                        disabled={isSubmitting || estaAssinado}
                        onChange={(valor) => setFieldValue('dsConteudo', valor)}
                        minHeight={460}
                        helperText={touched.dsConteudo && errors.dsConteudo}
                        error={touched.dsConteudo && !!errors.dsConteudo}
                      />
                    ) : (
                      <FormattedTextEditor
                        value={values.dsObservacao}
                        normalizarConteudo={normalizaConteudoReceituario}
                        editorSx={estiloEditorReceituario}
                        disabled={isSubmitting || estaAssinado}
                        onChange={(valor) =>
                          setFieldValue('dsObservacao', valor)
                        }
                        minHeight={460}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 16,
                  padding: 15,
                  height: '100%',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      size="small"
                      placeholder="Busque pelo nome"
                      value={buscaTexto}
                      disabled={isSubmitting || estaAssinado}
                      onChange={(e) =>
                        handleBuscarMedicamento(
                          e.target.value,
                          values.idTipoMedicamento,
                        )
                      }
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            size="small"
                            onClick={() => setMostrarFiltros(!mostrarFiltros)}
                          >
                            <FilterList fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />

                    {mostrarFiltros && (
                      <Box mt={1}>
                        <AutoComplete
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Tipo"
                          metodo="buscaTipoDeMedicamento"
                          disabled={isSubmitting || estaAssinado}
                          value={values.acTipoMedicamento}
                          onChange={(_, val) => {
                            setFieldValue('acTipoMedicamento', val)

                            const idTipo = Array.isArray(val) ? val[0][0] : ''

                            setFieldValue('idTipoMedicamento', idTipo)

                            handleBuscarMedicamento('', idTipo)
                          }}
                        />
                      </Box>
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    style={{ overflowY: 'auto', height: '70vh' }}
                  >
                    <Grid container spacing={1}>
                      {medicamentosBusca.map((item, index) => {
                        const id = item?.[0]?.[0]
                        const titulo = item?.[1]
                        const descricao = item?.[2] || ''
                        const posologia = item?.[6] || ''

                        return (
                          <Grid
                            item
                            xs={12}
                            key={id || index}
                            onClick={() => {
                              if (isSubmitting || estaAssinado) return

                              handleSelecionarMedicamento(
                                item,
                                setFieldValue,
                                values.dsConteudo,
                              )
                            }}
                            style={{
                              padding: 10,
                              border: '1px solid #e0e0e0',
                              borderRadius: 10,
                              marginLeft: 6,
                              marginTop: 5,
                              cursor: 'pointer',
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Typography
                                variant="subtitle2"
                                style={{
                                  fontSize: 12,
                                  fontWeight: 'bold',
                                  color: '#333',
                                }}
                              >
                                {titulo}
                              </Typography>
                              <IconButton
                                size="small"
                                aria-label="Editar medicamento"
                                disabled={isSubmitting || estaAssinado}
                                onClick={(event) =>
                                  handleEditarMedicamento(event, item)
                                }
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Box>
                            <FormattedHtml
                              html={descricao}
                              sx={{ fontSize: 11, color: '#888', mt: 0.5 }}
                            />
                            <FormattedHtml
                              html={posologia}
                              sx={{ fontSize: 11, color: '#888', mt: 0.5 }}
                            />
                          </Grid>
                        )
                      })}
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      position: 'fixed',
                      right: 24,
                      bottom: 24,
                      zIndex: 1200,
                      display: 'flex',
                      gap: 1,
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 4,
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        tpReceituarioRef.current = 'S'
                        submitForm()
                      }}
                      disabled={isSubmitting || estaAssinado}
                      startIcon={<Printer size={25} />}
                      endIcon={
                        isSubmitting ? (
                          <CircularProgress color="inherit" size={25} />
                        ) : null
                      }
                    >
                      Emitir simples
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        tpReceituarioRef.current = 'E'
                        submitForm()
                      }}
                      disabled={isSubmitting || estaAssinado}
                      startIcon={<Printer size={25} />}
                      endIcon={
                        isSubmitting ? (
                          <CircularProgress color="inherit" size={25} />
                        ) : null
                      }
                    >
                      Emitir controlado
                    </Button>
                  </Box>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

export default Edicao
